import { z } from 'zod';

// Bets (as Lagos Life's ⚽ LagosBet, worked out from its public client code), built to the rules in the
// development plan (Jerry, 20 October 2026: "we will add the betting app, just that we will follow
// regulations"). Everything is game money that can never be cashed out, so nothing can be won that is "money or
// money's worth" (Gambling Act 2005 s. 6). Every screen says so; players are 18+ and checked again here; there
// are limits per bet and per day, a player's own lower limit, breaks and a lasting switch-off, and a reality
// check. Odds are shown before every bet; results come from the server and are kept. Nothing here pushes
// betting: no alerts, no adverts.
//
// Three games: football (the same fixtures for everyone each day, results at 9 pm), high-low (is the next card
// higher or lower? a tie loses) and roulette (one zero).

const pence = (max: number) => z.number().int().min(0).max(max);

export const Team = z.object({
  id: z.string().regex(/^[a-z0-9_]{2,30}$/),
  name: z.string().trim().min(2).max(40),
  // how good they are (1 to 100): the odds and the results come from it
  strength: z.number().int().min(1).max(100),
});
export type Team = z.infer<typeof Team>;

export const BettingContent = z.object({
  visible: z.boolean(),
  name: z.string().trim().min(2).max(30),
  // what every screen says
  notice: z.string().trim().min(10).max(200),
  // where to get help (on the safer-play page)
  help: z.string().trim().min(10).max(300),
  // stakes: the smallest and largest bet, and the most staked in a London day
  minStakePence: pence(1_000_000_000),
  maxStakePence: pence(1_000_000_000),
  dailyStakePence: pence(10_000_000_000),
  // the house's margin built into the football odds and the high-low payouts
  margin: z.number().min(0).max(0.3),
  // football: the teams, how many games a day, and the hour (London) the results come in
  teams: z.array(Team).min(4).max(60),
  gamesPerDay: z.number().int().min(1).max(20),
  resultsHour: z.number().int().min(0).max(23),
  // a reality check after this many bets in a session (a session ends after 30 minutes without a bet)
  realityEvery: z.number().int().min(5).max(500),
});
export type BettingContent = z.infer<typeof BettingContent>;

// London's own fictional clubs (real clubs' names are their trademarks)
const club = (id: string, name: string, strength: number) => ({ id, name, strength });
export const DEFAULT_BETTING: BettingContent = {
  visible: true,
  name: 'London Bets',
  notice:
    "18+ · Just for fun: London Life money can't be withdrawn or swapped for real money. The house always has an edge.",
  help: 'Worried about gambling, in a game or for real? The National Gambling Helpline is free, 24 hours: 0808 8020 133 (GamCare).',
  minStakePence: 100,
  maxStakePence: 100_000,
  dailyStakePence: 500_000,
  margin: 0.06,
  teams: [
    club('docklands', 'Docklands Dockers', 78),
    club('thames', 'Thames Rovers', 82),
    club('eastend', 'East End Athletic', 70),
    club('greenwich', 'Greenwich Mariners', 74),
    club('canning', 'Canning Town Wanderers', 61),
    club('stratford', 'Stratford Olympic', 85),
    club('royal_docks', 'Royal Docks Rangers', 66),
    club('limehouse', 'Limehouse Lions', 58),
    club('poplar', 'Poplar Pirates', 55),
    club('bow', 'Bow Bells FC', 63),
    club('woolwich', 'Woolwich Ironworks', 72),
    club('isle_dogs', 'Isle of Dogs Harriers', 52),
  ],
  gamesPerDay: 5,
  resultsHour: 21,
  realityEvery: 50,
};
export const bettingOf = (content: { betting?: BettingContent }) => content.betting ?? DEFAULT_BETTING;
export function bettingProblems(betting: BettingContent): string[] {
  const problems: string[] = [];
  if (betting.minStakePence > betting.maxStakePence) problems.push('The smallest bet is more than the largest.');
  if (betting.maxStakePence > betting.dailyStakePence) problems.push('The largest bet is more than a day allows.');
  if (betting.teams.length < betting.gamesPerDay * 2) problems.push('There are not enough teams for the games.');
  const ids = new Set(betting.teams.map((team) => team.id));
  if (ids.size !== betting.teams.length) problems.push('Two teams have the same id.');
  return problems;
}

// ---------- the games' maths (the server decides; the screens show the same numbers) ----------

// a repeatable number from 0 to 1 from text (a day's fixtures are the same for everyone)
export function seededUnit(text: string) {
  let hash = 0x811c9dc5;
  for (const char of text) hash = Math.imul(hash ^ char.charCodeAt(0), 0x1000193);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x2c1b3c6d);
  hash ^= hash >>> 12;
  return (hash >>> 0) / 4_294_967_296;
}

export const FOOTBALL_PICKS = ['home', 'draw', 'away'] as const;
export type FootballPick = (typeof FOOTBALL_PICKS)[number];
// the chances of a home win, a draw and an away win from the two sides' strength (home advantage included)
export function matchChances(home: number, away: number) {
  const gap = (home + 6 - away) / 25;
  const draw = Math.max(0.18, 0.3 - Math.abs(gap) * 0.06);
  const homeShare = 1 / (1 + Math.exp(-gap));
  return { home: (1 - draw) * homeShare, draw, away: (1 - draw) * (1 - homeShare) };
}
// decimal odds with the margin, to two places
export const oddsOf = (chance: number, margin: number) =>
  Math.max(1.01, Math.floor((1 / (chance * (1 + margin))) * 100) / 100);
// the day's fixtures: the same for everyone, from the London day
export function fixturesFor(betting: BettingContent, day: string) {
  const order = [...betting.teams].sort(
    (a, b) => seededUnit(`${day}:${a.id}`) - seededUnit(`${day}:${b.id}`) || a.id.localeCompare(b.id),
  );
  return Array.from({ length: Math.min(betting.gamesPerDay, Math.floor(order.length / 2)) }, (_, index) => {
    const home = order[index * 2]!;
    const away = order[index * 2 + 1]!;
    const chances = matchChances(home.strength, away.strength);
    return {
      id: `${day}:${home.id}:${away.id}`,
      home,
      away,
      odds: {
        home: oddsOf(chances.home, betting.margin),
        draw: oddsOf(chances.draw, betting.margin),
        away: oddsOf(chances.away, betting.margin),
      },
      chances,
    };
  });
}
// a fixture's result, from a seed the server keeps (chosen when the fixtures are made, shown after)
export function resultOf(chances: { home: number; draw: number }, roll: number): FootballPick {
  return roll < chances.home ? 'home' : roll < chances.home + chances.draw ? 'draw' : 'away';
}

// cards: 2 to 14 (ace high), four suits
export const CARD_RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'] as const;
export const CARD_SUITS = ['♠', '♥', '♦', '♣'] as const;
export const cardName = (card: number) => `${CARD_RANKS[Math.floor(card / 4)]}${CARD_SUITS[card % 4]}`;
export const rankOf = (card: number) => Math.floor(card / 4) + 2;
// high-low: the chance the next card (from a fresh deck) is strictly higher or lower, and the payout with margin
export function highLowOdds(card: number, margin: number) {
  const rank = rankOf(card);
  const higher = ((14 - rank) * 4) / 51;
  const lower = ((rank - 2) * 4) / 51;
  return {
    higher: higher > 0 ? oddsOf(higher, margin) : null,
    lower: lower > 0 ? oddsOf(lower, margin) : null,
  };
}

// roulette: one zero; red numbers
export const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
export const ROULETTE_BETS = [
  'red',
  'black',
  'odd',
  'even',
  'low',
  'high',
  'dozen1',
  'dozen2',
  'dozen3',
  'number',
] as const;
export type RouletteBet = (typeof ROULETTE_BETS)[number];
// what a winning bet pays back (stake included), and whether a number wins it
export const ROULETTE_PAYS: Record<RouletteBet, number> = {
  red: 2,
  black: 2,
  odd: 2,
  even: 2,
  low: 2,
  high: 2,
  dozen1: 3,
  dozen2: 3,
  dozen3: 3,
  number: 36,
};
// what each bet is called on the table and in the bets list
export const ROULETTE_LABELS: Record<RouletteBet, string> = {
  red: 'Red',
  black: 'Black',
  odd: 'Odd',
  even: 'Even',
  low: '1–18',
  high: '19–36',
  dozen1: '1st 12',
  dozen2: '2nd 12',
  dozen3: '3rd 12',
  number: 'One number',
};
export function rouletteWins(bet: RouletteBet, spun: number, pick?: number) {
  if (bet === 'number') return spun === pick;
  if (spun === 0) return false;
  switch (bet) {
    case 'red':
      return RED.has(spun);
    case 'black':
      return !RED.has(spun);
    case 'odd':
      return spun % 2 === 1;
    case 'even':
      return spun % 2 === 0;
    case 'low':
      return spun <= 18;
    case 'high':
      return spun >= 19;
    case 'dozen1':
      return spun <= 12;
    case 'dozen2':
      return spun >= 13 && spun <= 24;
    default:
      return spun >= 25;
  }
}

// ---------- over the wire ----------

export const BREAKS = ['day', 'week', 'month', 'six_weeks', 'forever'] as const;
export type BreakLength = (typeof BREAKS)[number];
export const BREAK_DAYS: Record<Exclude<BreakLength, 'forever'>, number> = {
  day: 1,
  week: 7,
  month: 30,
  six_weeks: 42,
};

export const BetView = z.object({
  id: z.string().uuid(),
  game: z.enum(['football', 'highlow', 'roulette']),
  // what was backed, in words ("Thames Rovers to win", "Higher than 7♥", "Red")
  label: z.string(),
  stakePence: z.number().int(),
  odds: z.number(),
  status: z.enum(['open', 'won', 'lost']),
  paidPence: z.number().int(),
  // what happened ("2♣", "Spun 17", "Thames Rovers won")
  result: z.string().nullable(),
  placedAt: z.string(),
  settlesAt: z.string().nullable(),
});
export type BetView = z.infer<typeof BetView>;
export const FixtureView = z.object({
  id: z.string(),
  home: z.string(),
  away: z.string(),
  odds: z.object({ home: z.number(), draw: z.number(), away: z.number() }),
  // set once the day's results are in
  result: z.enum(FOOTBALL_PICKS).nullable(),
});
export type FixtureView = z.infer<typeof FixtureView>;
export const BettingReply = z.object({
  walletPence: z.number().int(),
  // today's football, and the time the results come in
  fixtures: z.array(FixtureView),
  resultsAt: z.string(),
  // today's games still take bets (until the results hour)
  footballOpen: z.boolean(),
  // the high-low card showing
  card: z.number().int().min(0).max(51),
  odds: z.object({ higher: z.number().nullable(), lower: z.number().nullable() }),
  bets: z.array(BetView),
  // what can still be staked today (the content's limit or the player's own, whichever is lower)
  stakedTodayPence: z.number().int(),
  leftTodayPence: z.number().int(),
  ownLimitPence: z.number().int().nullable(),
  // on a break (until when), or switched off for good
  breakUntil: z.string().nullable(),
  off: z.boolean(),
  // bets this session; a reality check is due
  sessionBets: z.number().int(),
  realityCheck: z.boolean(),
  message: z.string().optional(),
});
export type BettingReply = z.infer<typeof BettingReply>;

const stake = z.number().int().positive().max(1_000_000_000);
export const PlaceBetRequest = z.discriminatedUnion('game', [
  z.object({
    game: z.literal('football'),
    fixture: z.string().max(120),
    pick: z.enum(FOOTBALL_PICKS),
    stakePence: stake,
  }),
  z.object({ game: z.literal('highlow'), pick: z.enum(['higher', 'lower']), stakePence: stake }),
  z.object({
    game: z.literal('roulette'),
    bet: z.enum(ROULETTE_BETS),
    number: z.number().int().min(0).max(36).optional(),
    stakePence: stake,
  }),
]);
export type PlaceBetRequest = z.infer<typeof PlaceBetRequest>;
export const BettingControlRequest = z.discriminatedUnion('action', [
  // a lower daily limit of their own (null takes it off; raising it waits a day)
  z.object({ action: z.literal('limit'), dailyPence: z.number().int().min(0).max(10_000_000_000).nullable() }),
  z.object({ action: z.literal('break'), length: z.enum(BREAKS) }),
  // seen the reality check: carry on
  z.object({ action: z.literal('seen') }),
  // a new high-low card (free)
  z.object({ action: z.literal('card') }),
]);
export type BettingControlRequest = z.infer<typeof BettingControlRequest>;
